// Pure cookie helpers for the auth worker. No Cloudflare-runtime dependency —
// they take/return strings and numbers so the parse/build/expiry logic is unit-
// testable without a live worker (the untrusted `Cookie` header + base64url JWT
// payload are exactly the type-system boundaries testing-philosophy targets).

import {
  COOKIE_PATH,
  MAX_SESSION_SECONDS,
  SESSION_COOKIE,
  STATE_COOKIE,
  STATE_TTL_SECONDS,
  TRY_COOKIE,
  TRY_TTL_SECONDS,
} from "./config";

/** Parse a `Cookie` request header into a name→value map (RFC 6265 loose). */
export function parseCookies(header: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!header) return out;
  for (const part of header.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const name = part.slice(0, eq).trim();
    if (!name) continue;
    // First occurrence wins (RFC 6265 orders the more-specific/older cookie
    // first); don't let a later same-name entry overwrite it.
    if (name in out) continue;
    out[name] = part.slice(eq + 1).trim();
  }
  return out;
}

function serialize(
  name: string,
  value: string,
  maxAge: number,
  secure: boolean,
  httpOnly = true,
): string {
  const attrs = [`${name}=${value}`, `Path=${COOKIE_PATH}`];
  if (httpOnly) attrs.push("HttpOnly");
  attrs.push("SameSite=Lax", `Max-Age=${maxAge}`);
  // Host-only: deliberately NO `Domain` attribute (plan §3, SPEC "Auth") — the
  // session must never leak to sibling `sara.ai` hosts. `Secure` is omitted
  // only for local `http://localhost` dev, where browsers exempt it.
  if (secure) attrs.push("Secure");
  return attrs.join("; ");
}

/**
 * `Set-Cookie` value carrying the raw broker JWT. `maxAge` is derived by the
 * caller from the token's `exp` (see `sessionMaxAge`) so the cookie never
 * outlives the token it stores.
 */
export function buildSessionCookie(
  jwt: string,
  maxAge: number,
  secure: boolean,
): string {
  return serialize(SESSION_COOKIE, jwt, maxAge, secure);
}

/** `Set-Cookie` value that clears the session cookie (same attributes, Max-Age=0). */
export function clearSessionCookie(secure: boolean): string {
  return serialize(SESSION_COOKIE, "", 0, secure);
}

/** `Set-Cookie` value for the short-TTL circuit-breaker attempt counter. */
export function buildTryCookie(count: number, secure: boolean): string {
  return serialize(TRY_COOKIE, String(count), TRY_TTL_SECONDS, secure);
}

/** `Set-Cookie` value that clears the attempt counter (on success / at the cap). */
export function clearTryCookie(secure: boolean): string {
  return serialize(TRY_COOKIE, "", 0, secure);
}

/**
 * A minted sign-in nonce: base64url, so it survives the broker's
 * `URLSearchParams` re-serialization of the callback query unchanged (a plain
 * base64 `+` would come back as a space). The length range accepts the 22 chars
 * `STATE_BYTES` produces with room either side, and rejects anything that
 * couldn't be one of ours — including the empty string, which is what makes
 * "absent cookie" and "absent param" impossible to collide into a match.
 */
const STATE_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

/** Whether `value` could be a nonce this app minted. */
export function isWellFormedState(value: string): boolean {
  return STATE_PATTERN.test(value);
}

/**
 * `Set-Cookie` value carrying the one-shot sign-in nonce. Written from BOTH
 * sides: the worker (to clear it) and `signIn` in the page via
 * `document.cookie`, which is why it is the one cookie here that is not
 * `HttpOnly` (see `STATE_COOKIE`). Same host-only/`SameSite=Lax`/`Secure`
 * discipline as the rest — `Lax` is load-bearing, since it is what stops the
 * cookie riding a cross-site subresource request to `/auth/callback`.
 */
export function buildStateCookie(state: string, secure: boolean): string {
  return serialize(STATE_COOKIE, state, STATE_TTL_SECONDS, secure, false);
}

/** `Set-Cookie` value that clears the nonce — it is single-use. */
export function clearStateCookie(secure: boolean): string {
  return serialize(STATE_COOKIE, "", 0, secure, false);
}

/** Read the pending sign-in nonce, or `null` when absent or malformed. */
export function readState(cookies: Record<string, string>): string | null {
  const raw = cookies[STATE_COOKIE];
  if (raw === undefined || !isWellFormedState(raw)) return null;
  return raw;
}

/** Why a callback's `state` was refused, or `"ok"` when it checks out. */
export type StateCheck = "ok" | "missing_param" | "missing_cookie" | "mismatch";

/**
 * Compare the `state` a callback presented against the nonce this browser
 * stored when it started sign-in. FAIL-CLOSED and total: it returns a verdict
 * for every input and never throws.
 *
 * The `null` cases are enumerated separately and BEFORE any comparison on
 * purpose. A single `stored === presented` would pass `null === null` — i.e. a
 * forged callback to a browser that never started sign-in, which is precisely
 * the attack — and reduce the whole check to a no-op.
 *
 * `presented` is untrusted input; `isWellFormedState` bounds it before it is
 * compared or logged about, so no attacker-chosen string reaches either.
 */
export function checkState(
  stored: string | null,
  presented: string | null,
): StateCheck {
  if (presented === null || !isWellFormedState(presented)) {
    return "missing_param";
  }
  if (stored === null) return "missing_cookie";
  return stored === presented ? "ok" : "mismatch";
}

/** Read the current attempt count from the cookie map, coercing junk to 0. */
export function readTries(cookies: Record<string, string>): number {
  const raw = cookies[TRY_COOKIE];
  if (raw === undefined) return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function decodeBase64UrlToString(segment: string): string {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Read the `exp` (seconds since epoch) out of a JWT WITHOUT verifying it — used
 * only to cheaply reject a dead token before serving it and to size the cookie.
 * This is a base64url + JSON.parse boundary over an untrusted string: it must
 * return `null` on anything malformed, never throw.
 */
export function decodeJwtExp(jwt: string): number | null {
  const parts = jwt.split(".");
  const [, payloadPart] = parts;
  if (parts.length !== 3 || payloadPart === undefined) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(decodeBase64UrlToString(payloadPart));
  } catch {
    return null;
  }
  if (typeof payload !== "object" || payload === null) return null;
  const exp = (payload as Record<string, unknown>).exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
}

/**
 * Cookie `Max-Age` for a token expiring at `exp`: `min(exp - now, 7d)`, floored
 * at 0. Never negative (a past `exp` yields 0 → an immediately-expiring cookie).
 */
export function sessionMaxAge(exp: number, nowSeconds: number): number {
  const remaining = Math.floor(exp - nowSeconds);
  if (remaining <= 0) return 0;
  return Math.min(remaining, MAX_SESSION_SECONDS);
}
